import { useEffect, useState } from "react";
import { BarChart3, Lock, Mail, User as UserIcon } from "lucide-react";
import { PasswordInput } from "@/components/ui/PasswordInput";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { cn } from "@/lib/cn";

type View = "login" | "request" | "forgot" | "reset";

/** Pull a human-readable message out of an axios error. */
// Mirrors the server's MIN_PASSWORD_LENGTH (2026-10-12: Brandon's invite
// never completed — the rule has to be plain on the form, not a 422).
const MIN_PASSWORD = 10;

function errorMessage(err: unknown, fallback: string): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data
    ?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail) && detail.length && typeof detail[0]?.msg === "string") {
    return detail[0].msg;
  }
  return fallback;
}

function initialView(): View {
  if (typeof window === "undefined") return "login";
  return window.location.pathname.replace(/\/+$/, "") === "/reset-password"
    ? "reset"
    : "login";
}

function resetToken(): string | null {
  if (typeof window === "undefined") return null;
  return new URLSearchParams(window.location.search).get("token");
}

export function Login() {
  const { login } = useAuth();
  const [view, setView] = useState<View>(initialView);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [displayName, setDisplayName] = useState("");

  const [error, setError] = useState("");
  const [info, setInfo] = useState(() => {
    if (typeof window === "undefined") return "";
    if (new URLSearchParams(window.location.search).get("reset") === "success") {
      return "Password set — please sign in.";
    }
    // Set by the 401 handler when a live session expired mid-use.
    try {
      if (sessionStorage.getItem("rp.session_expired") === "1") {
        sessionStorage.removeItem("rp.session_expired");
        return "Your session expired — please sign in again.";
      }
    } catch { /* ignore */ }
    return "";
  });
  const [loading, setLoading] = useState(false);

  // After the reset reload (/?reset=success) clean the query off the URL.
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("reset")) {
      window.history.replaceState({}, "", "/");
    }
  }, []);

  const token = resetToken();
  const clear = () => {
    setError("");
    setInfo("");
  };
  const go = (v: View) => {
    setView(v);
    setError("");
    setInfo("");
    setPassword("");
    setConfirm("");
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    clear();
    try {
      await login(email.trim().toLowerCase(), password);
    } catch (err) {
      setError(errorMessage(err, "Invalid email or password"));
    } finally {
      setLoading(false);
    }
  };

  const handleRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < MIN_PASSWORD) {
      setError(`Password must be at least ${MIN_PASSWORD} characters.`);
      return;
    }
    setLoading(true);
    clear();
    try {
      await api.post("/auth/request-access", {
        email: email.trim().toLowerCase(),
        display_name: displayName.trim(),
        password,
      });
      go("login");
      setInfo(
        "Request submitted. If your account is already set up you can sign in now — otherwise an admin will review it.",
      );
    } catch (err) {
      setError(errorMessage(err, "Could not submit your request"));
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    clear();
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      go("login");
      setInfo("If that email belongs to an account, a reset link is on its way.");
    } catch (err) {
      setError(errorMessage(err, "Could not send the reset link"));
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < MIN_PASSWORD) {
      setError(`Password must be at least ${MIN_PASSWORD} characters.`);
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setLoading(true);
    clear();
    try {
      await api.post("/auth/reset-password", { token, password });
      // Full reload to a clean root URL. The router is a module-level
      // singleton initialised at page load; it captured /reset-password
      // (not a real route), and history.replaceState wouldn't update it —
      // so it would 404 once mounted after login. A reload re-inits at /.
      window.location.replace("/?reset=success");
      return;
    } catch (err) {
      setError(errorMessage(err, "This link is invalid or has expired."));
    } finally {
      setLoading(false);
    }
  };

  const inputCls = (invalid: boolean) =>
    cn(
      "w-full rounded-xl border bg-slate-800 py-3 pl-10 pr-4 text-sm text-white placeholder:text-slate-400 focus:outline-none focus:ring-2",
      invalid
        ? "border-red-500 focus:ring-red-500"
        : "border-slate-700 focus:ring-indigo-500",
    );

  const subtitle =
    view === "login"
      ? "Sign in to continue"
      : view === "request"
        ? "Request access"
        : view === "forgot"
          ? "Reset your password"
          : "Set a new password";

  const submitLabel = loading
    ? "Working…"
    : view === "login"
      ? "Sign in"
      : view === "request"
        ? "Request access"
        : view === "forgot"
          ? "Send reset link"
          : "Set password";

  const onSubmit =
    view === "login"
      ? handleLogin
      : view === "request"
        ? handleRequest
        : view === "forgot"
          ? handleForgot
          : handleReset;

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-900 px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-indigo-600">
            <BarChart3 className="h-7 w-7 text-white" />
          </div>
          <h1 className="text-xl font-bold text-white">Resource Planner</h1>
          <p className="mt-1 text-sm text-ink-subtle">{subtitle}</p>
        </div>

        {view === "reset" && !token ? (
          <div className="space-y-4 text-center">
            <p className="text-sm text-red-400">This reset link is missing its token.</p>
            <button
              onClick={() => window.location.replace("/")}
              className="text-xs text-ink-subtle hover:text-ink-ghost"
            >
              Back to sign in
            </button>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-4">
            {/* Email — shown on every view except reset */}
            {view !== "reset" && (
              <div className="relative">
                <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    clear();
                  }}
                  placeholder="Email"
                  autoComplete="email"
                  autoFocus
                  required
                  className={inputCls(Boolean(error))}
                />
              </div>
            )}

            {view === "request" && (
              <div className="relative">
                <UserIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted" />
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => {
                    setDisplayName(e.target.value);
                    clear();
                  }}
                  placeholder="Full name"
                  autoComplete="name"
                  required
                  className={inputCls(false)}
                />
              </div>
            )}

            {/* Password — shown on login, request, reset (not forgot) */}
            {view !== "forgot" && (
              <div className="relative">
                <PasswordInput
                  leadingIcon={<Lock className="h-4 w-4" />}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    clear();
                  }}
                  placeholder={view === "reset" ? "New password" : "Password"}
                  autoComplete={view === "login" ? "current-password" : "new-password"}
                  autoFocus={view === "reset"}
                  required
                  minLength={view === "login" ? undefined : MIN_PASSWORD}
                  aria-describedby={view === "login" ? undefined : "password-rule"}
                  className={inputCls(Boolean(error))}
                />
                {view !== "login" && (
                  <p id="password-rule" className="mt-1 pl-1 text-[11px] text-ink-muted" data-testid="password-rule">
                    At least {MIN_PASSWORD} characters.
                  </p>
                )}
              </div>
            )}

            {view === "reset" && (
              <div className="relative">
                <PasswordInput
                  leadingIcon={<Lock className="h-4 w-4" />}
                  value={confirm}
                  onChange={(e) => {
                    setConfirm(e.target.value);
                    clear();
                  }}
                  placeholder="Confirm new password"
                  autoComplete="new-password"
                  required
                  className={inputCls(Boolean(error))}
                />
              </div>
            )}

            {error && (
              <p className="text-center text-xs font-medium text-red-400">{error}</p>
            )}
            {info && (
              <p className="text-center text-xs font-medium text-emerald-400">{info}</p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-xl bg-indigo-600 py-3 text-sm font-semibold text-white transition-colors hover:bg-indigo-700 disabled:opacity-50"
            >
              {submitLabel}
            </button>
          </form>
        )}

        {/* Footer links */}
        {view === "login" && (
          <div className="mt-5 flex flex-col items-center gap-2 text-xs text-ink-subtle">
            <button
              type="button"
              onClick={() => go("forgot")}
              className="transition-colors hover:text-ink-ghost"
            >
              Forgot your password?
            </button>
            <button
              type="button"
              onClick={() => go("request")}
              className="transition-colors hover:text-ink-ghost"
            >
              Need access? Request an account
            </button>
          </div>
        )}
        {(view === "request" || view === "forgot") && (
          <button
            type="button"
            onClick={() => go("login")}
            className="mt-5 w-full text-center text-xs text-ink-subtle transition-colors hover:text-ink-ghost"
          >
            Back to sign in
          </button>
        )}
      </div>
    </div>
  );
}
